import { useEffect, useMemo, useState } from "react";
import i18n from "i18next";
import { ar, en } from "../translation";

function LanguageSwitcher({
  language,
  setLanguage,
}: {
  language: string;
  setLanguage: (lang: string) => void;
}) {
  const onToggle = () => {
    const newLang = language === "en" ? "ar" : "en";
    (i18n as any).changeLanguage(newLang);
    setLanguage(newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
  };
  return (
    <button
      onClick={onToggle}
      className="rounded-2xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur transition hover:bg-white/20"
    >
      {language === "en" ? "العربية" : "English"}
    </button>
  );
}

export default function Home() {
  const hash =
    window.location.pathname.replaceAll(/(\/|#)/g, "") || window.location.hash.replaceAll(/(\/|#)/g, "") ||
    window.location.hostname.split(".")[0];
  const initialLang = hash === "ar" ? "ar" : "en";
  const [language, setLanguage] = useState(initialLang);
  const [t, setT] = useState(() => (key: string) => key);

  useEffect(() => {
    i18n.init({
      resources: {
        en: { translation: en },
        ar: { translation: ar },
      },
      lng: language,
      fallbackLng: language,
      interpolation: { escapeValue: false },
    });
    setT(() => i18n.t as any);
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, []);

  const highlights = useMemo(
    () => [
      { title: t("h1Title"), description: t("h1Desc") },
      { title: t("h2Title"), description: t("h2Desc") },
      { title: t("h3Title"), description: t("h3Desc") },
    ],
    [t, language],
  );

  const contacts = useMemo(
    () => [
      { label: t("phone"), value: t("phoneValue") },
      { label: t("email"), value: t("emailValue") },
      {
        label: t("address"),
        value: t("addressValue"),
        url: "https://maps.app.goo.gl/4ek7c6t2ckQKjKsL6",
      },
      { label: t("hours"), value: t("hoursValue") },
    ],
    [t, language],
  );

  return (
    <div className="min-h-screen bg-neutral-100 text-neutral-900">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-slate-800 to-neutral-700" />
        <div className="absolute -top-20 -right-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 h-80 w-80 rounded-full bg-white/10 blur-3xl" />

        <div className="relative mx-auto max-w-6xl px-6 py-24 md:px-10 lg:px-12">
          <div className="mb-8 flex justify-end">
            <LanguageSwitcher language={language} setLanguage={setLanguage} />
          </div>

          <div
            className={` items-center gap-12 lg:grid-cols-2 grid ${language === "ar" ? "flex-row-reverse" : "flex-row"}`}
          >
            <div className={language === "ar" ? "lg:order-2" : ""}>
              <h1 className="mb-4 inline-block rounded-full border border-white/20 bg-white/10 px-4 py-1 text-sm text-white/90">
                {t("badge")}
              </h1>
              <h1 className="max-w-xl text-4xl font-semibold tracking-tight text-white md:text-6xl">
                {t("heroTitle")}
              </h1>
              <p className="mt-6 max-w-xl text-base leading-7 text-white/80 md:text-lg">
                {t("heroText")}
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href={`tel:${t("phoneValue")}`}
                  className="rounded-2xl bg-white px-6 py-3 text-sm font-medium text-neutral-900 shadow-lg transition hover:scale-[1.02]"
                >
                  {t("callUs")}
                </a>
                <a
                  href={`mailto:${t("emailValue")}`}
                  className="rounded-2xl border border-white/25 bg-white/10 px-6 py-3 text-sm font-medium text-white backdrop-blur transition hover:bg-white/20"
                >
                  {t("emailUs")}
                </a>
              </div>
            </div>

            <div className="grid gap-4">
              <img src={"/transparent.png"} alt="Magdi Hegazy Textile Logo" />
              <div className="rounded-[2rem] border border-white/15 bg-white/10 p-6 backdrop-blur-xl shadow-2xl">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl bg-white/10 p-5 text-white">
                    <p className="text-sm text-white/70">{t("countries")}</p>
                    <p className="mt-2 text-2xl font-semibold">
                      {t("countriesValue")}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white/10 p-5 text-white">
                    <p className="text-sm text-white/70">{t("clients")}</p>
                    <p className="mt-2 text-2xl font-semibold">
                      {t("clientsValue")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-18 md:px-10 lg:px-12">
        <div className="mb-8 flex items-end justify-between gap-6 mt-2">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-neutral-500">
              {t("offerLabel")}
            </p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight md:text-4xl">
              {t("offerTitle")}
            </h2>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3 mt-2">
          {highlights.map((item) => (
            <div
              key={item.title}
              className="rounded-[2rem] bg-white p-7 shadow-sm ring-1 ring-black/5 transition hover:-translate-y-1 hover:shadow-lg"
            >
              <h3 className="text-xl font-semibold">{item.title}</h3>
              <p className="mt-3 text-sm leading-7 text-neutral-600">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-20 md:px-10 lg:px-12">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr] mt-2">
          <div className="rounded-[2rem] bg-white p-8 shadow-sm ring-1 ring-black/5 mt-2">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-neutral-500">
              {t("aboutLabel")}
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight">
              {t("aboutTitle")}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-8 text-neutral-600">
              {t("aboutText1")}
            </p>
            <p className="mt-4 max-w-2xl text-base leading-8 text-neutral-600">
              {t("aboutText2")}
            </p>
          </div>

          <div className="rounded-[2rem] bg-neutral-900 p-8 text-white shadow-xl mt-2">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-white/60">
              {t("contactLabel")}
            </p>
            <div className="mt-6 space-y-5">
              {contacts.map((item) => (
                <div
                  key={item.label}
                  className="border-b border-white/10 pb-4 last:border-b-0"
                >
                  <p className="text-sm text-white/60">{item.label}</p>
                  <p className="mt-2 text-base font-medium text-white">
                    {item.url && window.location.protocol === "https:" ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.value}
                      </a>
                    ) : (
                      item.value
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
